/* eslint-disable prefer-const */
import {
  customersAuthorizationApi,
  ECustomerAuthorizationEndpoints
} from '../api';
import {
  IInvoicesResponse,
  IInvoiceData,
  IInvoiceBilletResponse,
  EInvoiceSituation,
  EInvoiceStatus,
  IInvoiceCreditsResponse
} from '../../store/ducks/invoices/types';
import { EGerdauService } from '../EGerdauService';
import moment from 'moment';
import { ICustomer } from '../../store/ducks/customers/types';
import { ECustomerTypeView } from '../../store/ducks/user/types';
import { IDownloadNFEResponse } from '../../store/ducks/long-steel-orders/types';

export class InvoicesService extends EGerdauService {
  async getDuplicates(
    customerId: string,
    customerViewType: ECustomerTypeView,
    startDate?: string,
    endDate?: string,
    selectedStatus?: string
  ): Promise<IInvoicesResponse> {
    let invoicesResponse: IInvoicesResponse;

    const headers = super.getHeaders();
    const requestURL: string = ECustomerAuthorizationEndpoints.INVOICES;

    const params = {
      customer: customerId,
      limit: 999,
      offset: 1,
      status: selectedStatus ?? '',
      customerType:
        customerViewType === ECustomerTypeView.EMITTER
          ? customerViewType
          : ECustomerTypeView.RECEIVER,
      startPaymentDate: startDate,
      startDate: startDate,
      endPaymentDate: endDate,
      endDate: endDate
    };

    if (selectedStatus === 'V') {
      delete params.startPaymentDate;
      delete params.startDate;
      delete params.endPaymentDate;
      delete params.endDate;
    }

    const response = await customersAuthorizationApi.get(requestURL, {
      headers,
      params
    });
    invoicesResponse = response.data;

    if (!invoicesResponse.invoices) {
      invoicesResponse.invoices = [];
    }

    this.setStatusInvoices(invoicesResponse.invoices);
    this.sortInvoicesByStatus(invoicesResponse.invoices);

    invoicesResponse.lastLoadTime = new Date().getTime();
    invoicesResponse.filterByStatus = selectedStatus;

    if (customerViewType === ECustomerTypeView.EMITTER) {
      invoicesResponse.receivers = this.getReceivers(invoicesResponse.invoices);
    } else {
      // customerViewType === ECustomerTypeView.RECEIVER
      invoicesResponse.emitters = this.getEmitters(invoicesResponse.invoices);
    }

    return invoicesResponse;
  }

  private setStatusInvoices(invoices: IInvoiceData[]) {
    invoices.forEach((invoice) => {
      if (invoice.situation === EInvoiceSituation.CLOSED) {
        invoice.status = EInvoiceStatus.CLOSED;
      } else if (Number(invoice.delayedInvoiceDays) <= 0) {
        invoice.status = EInvoiceStatus.WILL_DUE;
      } else {
        invoice.status = EInvoiceStatus.DUE;
      }
    });
  }

  private sortInvoicesByStatus(invoices: IInvoiceData[]) {
    const sortByStatus = (
      sortedInvoices: IInvoiceData[],
      status: EInvoiceStatus
    ): IInvoiceData[] => {
      return sortedInvoices.sort((a, b) =>
        a.status === status && b.status !== status ? -1 : 0
      );
    };

    const sortByDueDate = (sortedInvoices: IInvoiceData[]): IInvoiceData[] => {
      return sortedInvoices.sort(
        (a, b) =>
          moment(a.dueDate, 'YYYY-MM-DD').toDate().getTime() -
          moment(b.dueDate, 'YYYY-MM-DD').toDate().getTime()
      );
    };

    invoices = sortByDueDate(invoices);

    invoices = sortByStatus(invoices, EInvoiceStatus.CLOSED);
    invoices = sortByStatus(invoices, EInvoiceStatus.WILL_DUE);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    invoices = sortByStatus(invoices, EInvoiceStatus.DUE);
  }

  private getReceivers(invoices: IInvoiceData[]): ICustomer[] {
    const receivers: ICustomer[] = [];

    invoices.forEach((invoice) => {
      const isAlreadyAdd = receivers.some(
        (r) => r.customerId === invoice.receiver.code
      );
      const receiver = invoice.receiver;

      if (!isAlreadyAdd && receiver.code) {
        receivers.push({
          customerId: receiver.code,
          name: receiver.name,
          taxNumber: receiver.taxNumber,
          locality: {
            address: `${receiver.street} ${receiver.streetNumber}`,
            city: receiver.city,
            district: receiver.neighborhood,
            state: receiver.state,
            country: receiver.country,
            zipCode: receiver.zipCode,
            zipcode: receiver.zipCode
          },
          salesArea: [],
          division: []
        });
      }
    });

    return receivers;
  }

  private getEmitters(invoices: IInvoiceData[]): ICustomer[] {
    const emitters: ICustomer[] = [];

    invoices.forEach((invoice) => {
      const isAlreadyAdd = emitters.some(
        (e) => e.customerId === invoice.emitter.code
      );
      const emitter = invoice.emitter;

      if (emitter && !isAlreadyAdd && emitter.code) {
        emitters.push({
          customerId: emitter.code,
          name: emitter.name,
          taxNumber: emitter.taxNumber,
          locality: {
            address: `${emitter.street} ${emitter.streetNumber}`,
            city: emitter.city,
            district: emitter.neighborhood,
            state: emitter.state,
            country: emitter.country,
            zipCode: emitter.zipCode,
            zipcode: emitter.zipCode
          },
          salesArea: [],
          division: []
        });
      }
    });

    return emitters;
  }

  async loadNFEFileFromFinancials(
    customerId: string,
    invoiceItem: IInvoiceData,
    customerViewType: ECustomerTypeView
  ): Promise<IDownloadNFEResponse> {
    const headers = new EGerdauService().getHeaders();
    const { nfeSeries, billingNumber, invoiceDate } = invoiceItem;
    const customerType =
      customerViewType === ECustomerTypeView.EMITTER
        ? customerViewType
        : ECustomerTypeView.RECEIVER;

    const requestURL = [
      ECustomerAuthorizationEndpoints.GENERATE_BILLING,
      '?offset=1',
      '&limit=999',
      `&customer=${customerId}`,
      `&invoice=${nfeSeries}`,
      `&billing=${billingNumber}`,
      `&customerType=${customerType}`,
      `&invoiceDate=${invoiceDate.replace(/-/g, '')}`,
      `&financial=X`
    ];

    const response = await customersAuthorizationApi.get(requestURL.join(''), {
      headers
    });
    return response.data as IDownloadNFEResponse;
  }

  async getBilletInvoice(
    taxNumberAccount: string,
    dueDate: string,
    customerCnpj: string,
    bankSlipNumber: string,
    bankCode: string,
    invoiceType: string
  ): Promise<IInvoiceBilletResponse> {
    const headers = super.getHeaders();
    const requestURL = [
      ECustomerAuthorizationEndpoints.BILLETS,
      `?gerdauTaxNum=${taxNumberAccount}`,
      `&ourNumber=${bankSlipNumber}`,
      `&dueDate=${dueDate}`,
      `&customer=${customerCnpj}`,
      `&bankNum=${bankSlipNumber}`,
      `&bankCode=${bankCode}`,
      `&invoiceType=${invoiceType}`
    ];
    const response = await customersAuthorizationApi.get(requestURL.join(''), {
      headers
    });
    return response.data as IInvoiceBilletResponse;
  }

  async getInvoiceCredits(
    customerId: string
  ): Promise<IInvoiceCreditsResponse> {
    const headers = super.getHeaders();
    const response = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.INVOICE_CREDITS}?customer=${customerId}`,
      { headers }
    );

    const invoiceCreditsResponse: IInvoiceCreditsResponse = {
      invoices: response.data.invoices,
      creditSum: response.data.creditsSummary?.creditSum || ''
    };

    return invoiceCreditsResponse;
  }
}
